import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../config/api';
import { auth } from '../../firebase/config';
import { getViewerTimezone } from '../../utils/dateTime';
import './DisclosureTab.css';

// Local getFirebaseToken helper — every page defines its own per the project convention
// (see TransactionDocuments.js:316). Waits for onAuthStateChanged if auth.currentUser
// isn't populated yet.
const getFirebaseToken = () => new Promise((resolve, reject) => {
  if (auth.currentUser) {
    auth.currentUser.getIdToken(true).then(resolve).catch(reject);
    return;
  }
  const timeoutId = setTimeout(() => {
    unsubscribe();
    reject(new Error('Firebase auth timeout'));
  }, 10000);
  const unsubscribe = auth.onAuthStateChanged(async (user) => {
    clearTimeout(timeoutId);
    unsubscribe();
    if (user) {
      try {
        resolve(await user.getIdToken(true));
      } catch (e) {
        reject(e);
      }
    } else {
      reject(new Error('No authenticated user'));
    }
  });
});

const formatDate = (isoish) => {
  if (!isoish) return '';
  try {
    const d = new Date(isoish);
    if (Number.isNaN(d.getTime())) return isoish;
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
      timeZone: getViewerTimezone(),
    });
  } catch {
    return isoish;
  }
};

const formatBytes = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const DisclosureTab = ({ transaction }) => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [shares, setShares] = useState([]);
  const [recipientsOpen, setRecipientsOpen] = useState(false); // recipients panel shown below Share Links
  const [recipients, setRecipients] = useState([]); // merged across every share (active + revoked) for this transaction
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isLoadingShares, setIsLoadingShares] = useState(false);
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(false);
  const [isCreatingShare, setIsCreatingShare] = useState(false);
  const [copyState, setCopyState] = useState({}); // { [shareToken]: 'copied' | 'error' }
  const [smsPromptShareToken, setSmsPromptShareToken] = useState(null); // shareToken whose "Text" input is open
  const [smsPhoneNumber, setSmsPhoneNumber] = useState('');
  const [isSendingSms, setIsSendingSms] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: 'success', title: '', message: '' });
  // Branded confirm-dialog state — replaces window.confirm. Holds a single callback
  // so the same modal serves both file-delete and share-revoke.
  const [confirm, setConfirm] = useState({ visible: false, title: '', body: '', confirmLabel: 'Yes', danger: false, onConfirm: null, isBusy: false });
  // Per-folder cover-sheet metadata (Phase 1 backend). coverSheetGeneratedAt + pageCount
  // are null until the first time the PDF is generated via the preview endpoint — the
  // metadata row is cheap to fetch regardless.
  const [folderMetadata, setFolderMetadata] = useState(null);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [isEditingInstructions, setIsEditingInstructions] = useState(false);
  const [instructionsDraft, setInstructionsDraft] = useState('');
  const [isSavingInstructions, setIsSavingInstructions] = useState(false);
  const [isTogglingVisibility, setIsTogglingVisibility] = useState(false);
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const downloadMenuRef = useRef(null);
  const askConfirm = useCallback((opts) => {
    setConfirm({ visible: true, title: opts.title, body: opts.body, confirmLabel: opts.confirmLabel || 'Yes', danger: !!opts.danger, onConfirm: opts.onConfirm, isBusy: false });
  }, []);
  const closeConfirm = useCallback(() => {
    setConfirm((c) => (c.isBusy ? c : { visible: false, title: '', body: '', confirmLabel: 'Yes', danger: false, onConfirm: null, isBusy: false }));
  }, []);
  const runConfirm = useCallback(async () => {
    if (!confirm.onConfirm) return;
    setConfirm((c) => ({ ...c, isBusy: true }));
    try {
      await confirm.onConfirm();
    } finally {
      setConfirm({ visible: false, title: '', body: '', confirmLabel: 'Yes', danger: false, onConfirm: null, isBusy: false });
    }
  }, [confirm]);

  const fileInputRef = useRef(null);

  // Close the Download dropdown on any outside click — matches the share-link
  // menu pattern elsewhere, keeps the dropdown unobtrusive.
  useEffect(() => {
    if (!isDownloadMenuOpen) return undefined;
    const onDocClick = (e) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target)) {
        setIsDownloadMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [isDownloadMenuOpen]);

  const showNotification = (type, title, message) => {
    setNotification({ show: true, type, title, message });
    setTimeout(() => setNotification((n) => ({ ...n, show: false })), 4500);
  };

  // ================= Files (folder=disclosure) =================

  const fetchFiles = useCallback(async () => {
    if (!transaction?.id) return;
    setIsLoadingFiles(true);
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.get(
        `${API_ENDPOINTS.transactionFiles(transaction.id)}?folder=disclosure`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFiles(Array.isArray(res.data) ? res.data : (res.data?.files || []));
    } catch (e) {
      console.error('[DisclosureTab] fetchFiles failed', e);
      showNotification('error', 'Load Failed', 'Could not load disclosure files.');
    } finally {
      setIsLoadingFiles(false);
    }
  }, [transaction?.id]);

  // ================= Folder metadata (cover-sheet instructions + visibility) =================

  const fetchFolderMetadata = useCallback(async () => {
    if (!transaction?.id) return;
    setIsLoadingMetadata(true);
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.get(
        API_ENDPOINTS.folderMetadata(transaction.id, 'disclosure'),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFolderMetadata(res.data || null);
    } catch (e) {
      // 404 or empty = no metadata row yet; treat as default (hidden=false, no instructions).
      // Only log unexpected failures.
      if (e?.response?.status !== 404) {
        console.error('[DisclosureTab] fetchFolderMetadata failed', e);
      }
      setFolderMetadata(null);
    } finally {
      setIsLoadingMetadata(false);
    }
  }, [transaction?.id]);

  const openEditInstructions = () => {
    setInstructionsDraft(folderMetadata?.instructions || '');
    setIsEditingInstructions(true);
  };

  const handleSaveInstructions = async () => {
    if (!transaction?.id) return;
    setIsSavingInstructions(true);
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.patch(
        API_ENDPOINTS.folderInstructions(transaction.id, 'disclosure'),
        { instructions: instructionsDraft },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFolderMetadata(res.data || null);
      setIsEditingInstructions(false);
      showNotification('success', 'Saved', 'Cover sheet instructions updated.');
    } catch (err) {
      console.error('[DisclosureTab] save instructions failed', err);
      const msg = err?.response?.data?.message || 'Could not save instructions.';
      showNotification('error', 'Save Failed', msg);
    } finally {
      setIsSavingInstructions(false);
    }
  };

  const handleToggleHidden = async () => {
    if (!transaction?.id || isTogglingVisibility) return;
    const nextHidden = !(folderMetadata?.coverSheetHidden);
    setIsTogglingVisibility(true);
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.patch(
        API_ENDPOINTS.folderCoverSheetVisibility(transaction.id, 'disclosure'),
        { hidden: nextHidden },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFolderMetadata(res.data || null);
    } catch (err) {
      console.error('[DisclosureTab] toggle visibility failed', err);
      showNotification('error', 'Update Failed', 'Could not update visibility.');
    } finally {
      setIsTogglingVisibility(false);
    }
  };

  // Preview routes to the in-app preview page (styled chrome + embedded PDF) instead
  // of blurting out a raw PDF in a new tab — gives the agent a Back button and keeps
  // their place in the app. Dedicated route lives in CoverSheetPreviewPage.js.
  const handlePreviewCoverSheet = () => {
    if (!transaction?.id) return;
    navigate(`/transaction/${transaction.id}/disclosure/cover-sheet-preview`);
  };

  // Regenerate — forces a fresh PDF build server-side (the GET pdf endpoint
  // regenerates on cache miss / stale upstream data). For retry when the
  // first generation failed (network / server blip) or when the agent wants
  // to pick up newly-edited instructions without hitting Preview.
  const [isRegenerating, setIsRegenerating] = useState(false);
  const handleRegenerateCoverSheet = async () => {
    if (!transaction?.id || isRegenerating) return;
    setIsRegenerating(true);
    try {
      const token = await getFirebaseToken();
      // ?force=true bypasses the GCS cache so the backend actually
      // re-materializes the PDF + stamps a fresh coverSheetGeneratedAt.
      // Without it, repeat GETs return the cached PDF with the original
      // timestamp (see rialty-transaction-service CoverSheetServiceImpl).
      await apiClient.get(
        API_ENDPOINTS.folderCoverSheetPdf(transaction.id, 'disclosure'),
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          params: { force: true },
        }
      );
      // Refetch metadata so coverSheetGeneratedAt + coverSheetPageCount flip
      // the subtitle from "Auto-generated on preview or share" to the "N page
      // · Updated {ts}" variant.
      await fetchFolderMetadata();
      showNotification('success', 'Regenerated', 'Cover sheet is up to date.');
    } catch (err) {
      console.error('[DisclosureTab] regenerate cover sheet failed', err);
      showNotification('error', 'Failed', 'Could not regenerate cover sheet. Please try again.');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (picked.length === 0 || !transaction?.id) return;

    setIsUploading(true);
    const uploaded = [];
    const failed = [];
    try {
      const token = await getFirebaseToken();
      // Sequential, not Promise.all — same one-file-at-a-time backend endpoint as
      // before (no batch endpoint exists), just looped instead of stopping at [0].
      for (const file of picked) {
        try {
          const formData = new FormData();
          formData.append('file', file, file.name);
          formData.append('folder', 'disclosure');
          await axios.post(
            API_ENDPOINTS.uploadTransactionFile(transaction.id),
            formData,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          uploaded.push(file.name);
        } catch (err) {
          console.error('[DisclosureTab] upload failed', file.name, err);
          failed.push(file.name);
        }
      }
      await fetchFiles();
      if (failed.length === 0) {
        showNotification('success', 'Uploaded', uploaded.length === 1
          ? `"${uploaded[0]}" added to Disclosure.`
          : `${uploaded.length} files added to Disclosure.`);
      } else if (uploaded.length === 0) {
        showNotification('error', 'Upload Failed', failed.length === 1
          ? `Could not upload "${failed[0]}". Please try again.`
          : `Could not upload ${failed.length} files. Please try again.`);
      } else {
        showNotification('error', 'Partially Uploaded', `${uploaded.length} uploaded, but ${failed.length} failed: ${failed.join(', ')}`);
      }
    } finally {
      setIsUploading(false);
    }
  };

  // ================= Shares =================

  const fetchShares = useCallback(async () => {
    if (!transaction?.id) return;
    setIsLoadingShares(true);
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.get(
        API_ENDPOINTS.disclosureShares(transaction.id),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShares(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('[DisclosureTab] fetchShares failed', e);
      showNotification('error', 'Load Failed', 'Could not load share links.');
    } finally {
      setIsLoadingShares(false);
    }
  }, [transaction?.id]);

  const handleCreateShare = async () => {
    if (!transaction?.id) return;
    // Belt-and-suspenders against the "double-click before disabled prop
    // propagates" race — the button also carries `disabled={activeShares.length>0}`.
    if (shares.some((s) => !s.isRevoked)) {
      showNotification('error', 'Already Active', 'Revoke the current share link before creating a new one.');
      return;
    }
    setIsCreatingShare(true);
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.post(
        API_ENDPOINTS.disclosureShares(transaction.id),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const created = res.data;
      setShares((prev) => [created, ...prev]);
      showNotification('success', 'Link Ready', 'Share this link with the recipient.');
      // auto-copy the newly-minted link for convenience
      handleCopy(created);
    } catch (err) {
      console.error('[DisclosureTab] create share failed', err);
      showNotification('error', 'Share Failed', err?.response?.data?.message || 'Could not create share link.');
    } finally {
      setIsCreatingShare(false);
    }
  };

  const handleRevoke = (share) => {
    if (!transaction?.id) return;
    askConfirm({
      title: 'Revoke this link?',
      body: 'Anyone with the URL will lose access immediately. This can\'t be undone — you\'d need to create a new share link.',
      confirmLabel: 'Revoke',
      danger: true,
      onConfirm: async () => {
        try {
          const token = await getFirebaseToken();
          const res = await apiClient.post(
            API_ENDPOINTS.disclosureShareRevoke(transaction.id, share.shareToken),
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setShares((prev) => prev.map((s) => (s.shareToken === share.shareToken ? res.data : s)));
          showNotification('success', 'Revoked', 'Link is no longer active.');
        } catch (err) {
          console.error('[DisclosureTab] revoke failed', err);
          showNotification('error', 'Revoke Failed', 'Please try again.');
        }
      },
    });
  };

  const handleCopy = async (share) => {
    try {
      await navigator.clipboard.writeText(share.shareUrl);
      setCopyState((s) => ({ ...s, [share.shareToken]: 'copied' }));
      setTimeout(() => setCopyState((s) => ({ ...s, [share.shareToken]: undefined })), 1800);
    } catch {
      setCopyState((s) => ({ ...s, [share.shareToken]: 'error' }));
      setTimeout(() => setCopyState((s) => ({ ...s, [share.shareToken]: undefined })), 1800);
    }
  };

  const toggleSmsPrompt = (shareToken) => {
    setSmsPromptShareToken((prev) => (prev === shareToken ? null : shareToken));
    setSmsPhoneNumber('');
  };

  const handleSendSms = async (share) => {
    if (!transaction?.id || !smsPhoneNumber.trim()) return;
    setIsSendingSms(true);
    try {
      const token = await getFirebaseToken();
      await apiClient.post(
        API_ENDPOINTS.disclosureShareSendSms(transaction.id, share.shareToken),
        { phoneNumber: smsPhoneNumber.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showNotification('success', 'Texted', `Link sent to ${smsPhoneNumber.trim()}.`);
      setSmsPromptShareToken(null);
      setSmsPhoneNumber('');
    } catch (err) {
      showNotification('error', 'Text Failed', err?.response?.data?.message || 'Could not send the text. Please try again.');
    } finally {
      setIsSendingSms(false);
    }
  };

  const handleDeleteFile = (file) => {
    const label = file.fileName || 'this document';
    const documentId = file.documentId;
    if (!documentId) {
      showNotification('error', 'Delete Failed', 'Cannot identify this document.');
      return;
    }
    askConfirm({
      title: 'Delete this document?',
      body: `"${label}" will be removed from the disclosure folder. Anyone with an active share link will no longer see it.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        // Optimistic remove; roll back on failure.
        const previous = files;
        setFiles((prev) => prev.filter((f) => f.documentId !== documentId));
        try {
          const token = await getFirebaseToken();
          await apiClient.delete(
            API_ENDPOINTS.deleteTransactionFile(transaction.id, documentId),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          showNotification('success', 'Deleted', `"${label}" was removed.`);
        } catch (err) {
          console.error('[DisclosureTab] delete failed', err);
          setFiles(previous);
          showNotification('error', 'Delete Failed', err?.response?.data?.message || 'Please try again.');
        }
      },
    });
  };

  const handleOpenRecipients = async () => {
    setRecipientsOpen(true);
    setRecipients([]);
    setIsLoadingRecipients(true);
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.get(
        API_ENDPOINTS.disclosureAllRecipients(transaction.id),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRecipients(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('[DisclosureTab] recipients failed', e);
      showNotification('error', 'Load Failed', 'Could not load recipients.');
    } finally {
      setIsLoadingRecipients(false);
    }
  };

  const handleCloseRecipients = () => {
    setRecipientsOpen(false);
    setRecipients([]);
  };

  // ================= Initial load =================

  useEffect(() => { fetchFiles(); }, [fetchFiles]);
  useEffect(() => { fetchShares(); }, [fetchShares]);
  useEffect(() => { fetchFolderMetadata(); }, [fetchFolderMetadata]);

  // Auto-generate the cover sheet on first visit to Disclosures (user ask
  // 2026-10-29). Previously the backend only materialised the PDF when the
  // agent clicked Preview or Share — but the paired tx-service PR auto-
  // registers the generated PDF as the first entry in Transaction Docs →
  // Disclosures, so it needs to exist by the time the agent lands on the
  // tab. We trigger a non-force GET on the pdf endpoint (cached hits short-
  // circuit; a true cache miss runs generateAndCache which also fires the
  // document upsert). Guarded by a ref so the auto-generation only fires
  // once per tab-session — the focus/visibility listener below would
  // otherwise re-trigger on every return.
  const coverSheetTriggeredRef = useRef(false);
  useEffect(() => {
    if (coverSheetTriggeredRef.current) return;
    if (!transaction?.id) return;
    // Wait until metadata has resolved — fetchFolderMetadata sets the state
    // to null on 404 (no row yet) so we must distinguish "still loading"
    // (isLoadingMetadata) from "loaded with no cover sheet".
    if (isLoadingMetadata) return;
    if (folderMetadata?.coverSheetGeneratedAt) return;

    coverSheetTriggeredRef.current = true;
    (async () => {
      try {
        const token = await getFirebaseToken();
        await apiClient.get(
          API_ENDPOINTS.folderCoverSheetPdf(transaction.id, 'disclosure'),
          { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' },
        );
        // Refetch so the subtitle flips to "N page · Updated {ts}" AND the
        // auto-registered cover-sheet row shows up in the Transaction Docs
        // list the next time the agent switches to that tab.
        await fetchFolderMetadata();
        await fetchFiles();
      } catch (err) {
        // Non-fatal: Regenerate button still works, and the Preview /
        // Share flows will generate on demand. Reset the guard so a focus
        // event (which refetches metadata) can retry once.
        console.warn('[DisclosureTab] auto cover-sheet generation failed', err);
        coverSheetTriggeredRef.current = false;
      }
    })();
  }, [transaction?.id, isLoadingMetadata, folderMetadata?.coverSheetGeneratedAt,
      fetchFolderMetadata, fetchFiles]);

  // Refetch cover-sheet metadata when the tab regains focus or the page
  // becomes visible again — covers the common flow where the user hits
  // Preview (which lazy-generates the PDF server-side), lands on the
  // CoverSheetPreviewPage, then returns to this tab. Without this
  // listener the subtitle stays stuck on "Auto-generated on preview or
  // share" even though coverSheetGeneratedAt is now populated on the
  // backend.
  useEffect(() => {
    const onFocus = () => fetchFolderMetadata();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchFolderMetadata();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchFolderMetadata]);

  // Agent-driven reorder: assign explicit sequential displayOrder values across the
  // whole folder for a single click (small list — N PATCHes beats introducing a
  // batch endpoint). After the writes, refetch so the server-side sort order drives
  // the visible list rather than inferring it client-side.
  const persistOrder = useCallback(async (orderedDocIds) => {
    if (!transaction?.id || orderedDocIds.length === 0) return;
    setIsReordering(true);
    try {
      const token = await getFirebaseToken();
      for (let i = 0; i < orderedDocIds.length; i += 1) {
        // Sequential rather than Promise.all — the backend writes serially to Firestore
        // per PATCH anyway, and a serial loop keeps "Document not found" errors scoped
        // to a single call so the surviving rows still get their new order.
        // eslint-disable-next-line no-await-in-loop
        await apiClient.patch(
          API_ENDPOINTS.updateTransactionFileOrder(transaction.id, orderedDocIds[i]),
          { displayOrder: i },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      await fetchFiles();
    } catch (err) {
      console.error('[DisclosureTab] reorder failed', err);
      showNotification('error', 'Reorder Failed', 'Could not update document order.');
    } finally {
      setIsReordering(false);
    }
  }, [transaction?.id, fetchFiles]);

  // ------- Drag + drop reorder (HTML5 native) -------
  // No new dep. draggedIndex is the row currently being dragged;
  // dragOverIndex drives the visual drop indicator. Both reset on dragEnd.
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragStart = (index) => (e) => {
    setDraggedIndex(index);
    // Required for Firefox to let the drag start at all.
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(index)); } catch { /* some browsers restrict */ }
  };

  const handleDragOver = (index) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) setDragOverIndex(index);
  };

  const handleDrop = (targetIndex) => (e) => {
    e.preventDefault();
    const sourceIndex = draggedIndex;
    setDraggedIndex(null);
    setDragOverIndex(null);
    if (sourceIndex == null || sourceIndex === targetIndex) return;
    const next = [...files];
    const [moved] = next.splice(sourceIndex, 1);
    // When dragging downward, the splice above has shifted the target up by one.
    const insertAt = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
    next.splice(insertAt, 0, moved);
    persistOrder(next.map((f) => f.documentId));
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleMove = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= files.length) return;
    const next = [...files];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    persistOrder(next.map((f) => f.documentId));
  };

  // Blob round-trip for both download options so the Firebase bearer rides on
  // the authenticated fetch rather than a bare href. Shared implementation —
  // caller supplies the endpoint URL + download filename.
  const downloadBlobViaFetch = async (endpointUrl, downloadFileName) => {
    const token = await getFirebaseToken();
    const res = await axios.get(endpointUrl, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });
    const blobUrl = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = downloadFileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
  };

  const handleDownloadCombinedPdf = async () => {
    if (!transaction?.id) return;
    setIsDownloadMenuOpen(false);
    try {
      await downloadBlobViaFetch(
        API_ENDPOINTS.folderCombinedPdf(transaction.id, 'disclosure'),
        'disclosures.pdf'
      );
    } catch (err) {
      console.error('[DisclosureTab] combined PDF download failed', err);
      showNotification('error', 'Download Failed', 'Could not download combined PDF.');
    }
  };

  const handleDownloadZip = async () => {
    if (!transaction?.id) return;
    setIsDownloadMenuOpen(false);
    try {
      await downloadBlobViaFetch(
        API_ENDPOINTS.downloadTransactionFolder(transaction.id, 'disclosure'),
        'disclosures.zip'
      );
    } catch (err) {
      console.error('[DisclosureTab] zip download failed', err);
      showNotification('error', 'Download Failed', 'Could not download ZIP.');
    }
  };

  const activeShares = React.useMemo(() => shares.filter((s) => !s.isRevoked), [shares]);
  const revokedShares = shares.filter((s) => s.isRevoked);

  // Most-recently-added recipient first. createdAt (when they submitted the
  // info form) is the one timestamp every recipient has — verifiedAt/
  // lastViewedAt are still null for anyone who hasn't finished OTP yet.
  const sortedRecipients = React.useMemo(
    () => [...recipients].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [recipients]
  );

  return (
    <div className="disclosure-tab">
      {notification.show && (
        <div className={`disclosure-notification disclosure-notification-${notification.type}`}>
          <strong>{notification.title}</strong>
          <p>{notification.message}</p>
        </div>
      )}

      <div className="disclosure-header">
        <div>
          <h2>Disclosures</h2>
          <p className="disclosure-subtitle">Upload disclosures, then share a secure link with any recipient. Recipients verify with a code sent to their email before viewing.</p>
        </div>
        <div className="disclosure-header-actions">
          <button className="disclosure-btn-secondary" onClick={handleUploadClick} disabled={isUploading}>
            {isUploading ? 'Uploading…' : '+ Upload Document'}
          </button>
          <div className="disclosure-download-dropdown" ref={downloadMenuRef}>
            <button
              type="button"
              className="disclosure-btn-secondary"
              onClick={() => setIsDownloadMenuOpen((v) => !v)}
              disabled={files.length === 0}
              aria-haspopup="menu"
              aria-expanded={isDownloadMenuOpen}
            >
              Download ▾
            </button>
            {isDownloadMenuOpen && (
              <div className="disclosure-download-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="disclosure-download-menu-item"
                  onClick={handleDownloadCombinedPdf}
                >
                  <span className="disclosure-download-menu-item-title">Combined PDF</span>
                  <span className="disclosure-download-menu-item-subtitle">
                    One file: cover sheet first, then documents in package order
                  </span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="disclosure-download-menu-item"
                  onClick={handleDownloadZip}
                >
                  <span className="disclosure-download-menu-item-title">Separate PDFs (.zip)</span>
                  <span className="disclosure-download-menu-item-subtitle">
                    Each document as its own file, cover sheet included
                  </span>
                </button>
              </div>
            )}
          </div>
          {activeShares.length > 0 && (
            <div className="disclosure-share-disabled-note" role="note">
              Revoke the active link before creating a new one.
            </div>
          )}
          <button
            className="disclosure-btn-primary"
            onClick={handleCreateShare}
            disabled={isCreatingShare || activeShares.length > 0}
            title={
              activeShares.length > 0
                ? 'An active share link already exists. Revoke it before creating a new one.'
                : undefined
            }
          >
            {isCreatingShare ? 'Creating…' : 'Share Disclosures Link'}
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
          multiple
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </div>

      {/* Files list */}
      <div className="disclosure-section">
        <h3>Files ({files.length})</h3>

        {/* Pinned cover-sheet row — first child of the folder, non-draggable,
            cannot be deleted (FR-1 / FR-10). The row is shown even when the PDF
            hasn't been generated yet; Preview triggers lazy generation. */}
        {!isLoadingMetadata && (
          <div className={`disclosure-coversheet-row ${folderMetadata?.coverSheetHidden ? 'is-hidden-from-viewers' : ''}`}>
            <div className="disclosure-coversheet-left">
              <span className="disclosure-coversheet-pin" aria-hidden="true">📌</span>
              <div className="disclosure-coversheet-info">
                <div className="disclosure-coversheet-title">
                  Cover sheet
                  {folderMetadata?.coverSheetHidden && (
                    <span className="disclosure-coversheet-badge">Hidden from viewers</span>
                  )}
                </div>
                <div className="disclosure-coversheet-meta">
                  {folderMetadata?.coverSheetPageCount
                    ? `${folderMetadata.coverSheetPageCount} page · Updated ${formatDate(folderMetadata.coverSheetGeneratedAt)}`
                    : 'Auto-generated on preview or share'}
                </div>
              </div>
            </div>
            <div className="disclosure-coversheet-actions">
              <button type="button" className="disclosure-btn-ghost" onClick={handlePreviewCoverSheet}>
                Preview
              </button>
              <button
                type="button"
                className="disclosure-btn-ghost"
                onClick={handleRegenerateCoverSheet}
                disabled={isRegenerating}
                title="Regenerate the cover sheet now (useful if an earlier attempt failed)"
              >
                {isRegenerating ? 'Regenerating…' : 'Regenerate'}
              </button>
              <button type="button" className="disclosure-btn-ghost" onClick={openEditInstructions}>
                Edit instructions
              </button>
              <label
                className={`disclosure-coversheet-toggle ${isTogglingVisibility ? 'is-busy' : ''}`}
                title={folderMetadata?.coverSheetHidden ? 'Show cover sheet to viewers' : 'Hide cover sheet from viewers'}
              >
                <input
                  type="checkbox"
                  checked={!!folderMetadata?.coverSheetHidden}
                  onChange={handleToggleHidden}
                  disabled={isTogglingVisibility}
                />
                <span className="disclosure-coversheet-toggle-track" />
                <span className="disclosure-coversheet-toggle-label">
                  Hide from viewers
                </span>
              </label>
            </div>
          </div>
        )}

        {isLoadingFiles ? (
          <p className="disclosure-empty">Loading…</p>
        ) : files.length === 0 ? (
          <p className="disclosure-empty">No files yet. Upload disclosure documents to get started.</p>
        ) : (
          <ul className="disclosure-file-list">
            {files.map((f, index) => (
              <li
                key={f.documentId || f.gcsPath}
                className={`disclosure-file-item${
                  draggedIndex === index ? ' is-dragging' : ''
                }${dragOverIndex === index && draggedIndex != null && draggedIndex !== index ? ' is-drop-target' : ''}`}
                draggable={!!f.documentId && !isReordering}
                onDragStart={handleDragStart(index)}
                onDragOver={handleDragOver(index)}
                onDrop={handleDrop(index)}
                onDragEnd={handleDragEnd}
                onDragLeave={() => dragOverIndex === index && setDragOverIndex(null)}
              >
                <span
                  className="disclosure-file-drag-handle"
                  aria-hidden="true"
                  title="Drag to reorder"
                >
                  ⋮⋮
                </span>
                <div className="disclosure-file-info">
                  <span className="disclosure-file-name">{f.fileName}</span>
                  <span className="disclosure-file-meta">
                    {formatBytes(f.fileSize)} · {f.uploadedAtDisplay || formatDate(f.uploadedAt)}
                  </span>
                </div>
                <div className="disclosure-file-row-actions">
                  {/* Up/down buttons alongside the drag handle. Drag is for quick
                      multi-row moves, buttons for single-step bumps + a11y / keyboard. */}
                  <button
                    type="button"
                    className="disclosure-file-reorder"
                    onClick={() => handleMove(index, -1)}
                    disabled={index === 0 || isReordering || !f.documentId}
                    aria-label={`Move ${f.fileName} up`}
                    title="Move up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="disclosure-file-reorder"
                    onClick={() => handleMove(index, 1)}
                    disabled={index === files.length - 1 || isReordering || !f.documentId}
                    aria-label={`Move ${f.fileName} down`}
                    title="Move down"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="disclosure-file-delete"
                    onClick={() => handleDeleteFile(f)}
                    aria-label={`Delete ${f.fileName}`}
                    disabled={!f.documentId || isReordering}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Share links */}
      <div className="disclosure-section">
        <div className="disclosure-section-header-row">
          <h3>Share Links ({activeShares.length} active{revokedShares.length ? `, ${revokedShares.length} revoked` : ''})</h3>
          {shares.length > 0 && (
            <button
              className="disclosure-btn-ghost"
              onClick={() => (recipientsOpen ? handleCloseRecipients() : handleOpenRecipients())}
            >
              {recipientsOpen ? 'Hide Recipients' : 'View All Recipients'}
            </button>
          )}
        </div>
        {isLoadingShares ? (
          <p className="disclosure-empty">Loading…</p>
        ) : shares.length === 0 ? (
          <p className="disclosure-empty">No share links yet. Click "Share Disclosures Link" to create one.</p>
        ) : (
          <>
            {activeShares.length === 0 ? (
              <p className="disclosure-empty">No active share links.</p>
            ) : (
              <ul className="disclosure-share-list">
                {activeShares.map((s) => (
                  <li key={s.shareToken} className="disclosure-share-item">
                    <div className="disclosure-share-row">
                      <div className="disclosure-share-link-block">
                        <div className="disclosure-share-url">{s.shareUrl}</div>
                        <div className="disclosure-share-meta">Created {s.createdAtDisplay || formatDate(s.createdAt)}</div>
                      </div>
                      <div className="disclosure-share-actions">
                        <button className="disclosure-btn-ghost" onClick={() => handleCopy(s)}>
                          {copyState[s.shareToken] === 'copied' ? 'Copied ✓' : copyState[s.shareToken] === 'error' ? 'Copy failed' : 'Copy'}
                        </button>
                        <button className="disclosure-btn-ghost" onClick={() => toggleSmsPrompt(s.shareToken)}>
                          {smsPromptShareToken === s.shareToken ? 'Cancel' : 'Text'}
                        </button>
                        <button className="disclosure-btn-danger" onClick={() => handleRevoke(s)}>
                          Revoke
                        </button>
                      </div>
                    </div>
                    {smsPromptShareToken === s.shareToken && (
                      <div className="disclosure-share-sms-prompt">
                        <input
                          type="tel"
                          className="disclosure-share-sms-input"
                          placeholder="Recipient's phone number"
                          value={smsPhoneNumber}
                          onChange={(e) => setSmsPhoneNumber(e.target.value)}
                          disabled={isSendingSms}
                          autoFocus
                        />
                        <button
                          className="disclosure-btn-primary"
                          onClick={() => handleSendSms(s)}
                          disabled={isSendingSms || !smsPhoneNumber.trim()}
                        >
                          {isSendingSms ? 'Sending…' : 'Send'}
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {revokedShares.length > 0 && (
              <>
                <h4 className="disclosure-subheading">Revoked ({revokedShares.length})</h4>
                <p className="disclosure-subhint">These links no longer work. They stay listed so the recipient audit trail is preserved.</p>
                <ul className="disclosure-share-list">
                  {revokedShares.map((s) => (
                    <li key={s.shareToken} className="disclosure-share-item revoked">
                      <div className="disclosure-share-row">
                        <div className="disclosure-share-link-block">
                          <div className="disclosure-share-header-row">
                            <span className="disclosure-badge-revoked">Revoked</span>
                          </div>
                          <div className="disclosure-share-url disclosure-share-url-strike">{s.shareUrl}</div>
                          <div className="disclosure-share-meta">
                            Created {s.createdAtDisplay || formatDate(s.createdAt)}
                            {(s.revokedAtDisplay || s.revokedAt) ? ` · Revoked ${s.revokedAtDisplay || formatDate(s.revokedAt)}` : ''}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>

      {/* Recipients — inline panel, directly under Share Links, not a popup.
          Merged across every share (active + revoked) for this transaction. */}
      {recipientsOpen && (
        <div className="disclosure-section disclosure-recipients-panel">
          <div className="disclosure-recipients-panel-header">
            <div>
              <h3>Recipients</h3>
              <p className="disclosure-modal-sub">Across all {shares.length} share link{shares.length === 1 ? '' : 's'} for this transaction</p>
              <p className="disclosure-modal-count">
                {recipients.length === 0
                  ? 'No verifications yet'
                  : `${recipients.length} recipient${recipients.length === 1 ? '' : 's'}`}
              </p>
            </div>
            <button className="disclosure-btn-ghost" onClick={handleCloseRecipients}>Hide</button>
          </div>
          {isLoadingRecipients ? (
            <p className="disclosure-empty">Loading…</p>
          ) : recipients.length === 0 ? (
            <div className="disclosure-empty-recipients">
              <div className="disclosure-empty-recipients-icon" aria-hidden="true">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="disclosure-empty-recipients-title">No one has verified yet</div>
              <div className="disclosure-empty-recipients-body">
                Recipients you share this link with will appear here after they enter their email code.
              </div>
            </div>
          ) : (
            <ul className="disclosure-recipients-list">
              {sortedRecipients.map((r) => {
                const displayName = [r.firstName, r.lastName].filter(Boolean).join(' ') || r.email;
                const initials = ((r.firstName?.[0] || '') + (r.lastName?.[0] || '')).toUpperCase() || (r.email?.[0] || '?').toUpperCase();
                const isVerified = !!(r.verifiedAt || r.verifiedAtDisplay);
                return (
                  <li key={r.recipientId} className="disclosure-recipient-card">
                    <div className="disclosure-recipient-header">
                      <div className="disclosure-recipient-avatar">{initials}</div>
                      <div className="disclosure-recipient-headline">
                        <div className="disclosure-recipient-name">{displayName}</div>
                        <span className={`disclosure-recipient-status ${isVerified ? 'verified' : 'pending'}`}>
                          {isVerified ? '● Verified' : '○ Pending'}
                        </span>
                      </div>
                    </div>
                    <div className="disclosure-recipient-details">
                      <div className="disclosure-recipient-detail">
                        <span className="disclosure-recipient-detail-label">Email</span>
                        <span className="disclosure-recipient-detail-value">{r.email}</span>
                      </div>
                      {r.phone && (
                        <div className="disclosure-recipient-detail">
                          <span className="disclosure-recipient-detail-label">Phone</span>
                          <span className="disclosure-recipient-detail-value">{r.phone}</span>
                        </div>
                      )}
                      <div className="disclosure-recipient-detail">
                        <span className="disclosure-recipient-detail-label">Requested</span>
                        <span className="disclosure-recipient-detail-value">
                          {r.createdAtDisplay || formatDate(r.createdAt)}
                        </span>
                      </div>
                      {isVerified && (
                        <div className="disclosure-recipient-detail">
                          <span className="disclosure-recipient-detail-label">Verified</span>
                          <span className="disclosure-recipient-detail-value">
                            {r.verifiedAtDisplay || formatDate(r.verifiedAt)}
                          </span>
                        </div>
                      )}
                      {(r.lastViewedAtDisplay || r.lastViewedAt) && (
                        <div className="disclosure-recipient-detail">
                          <span className="disclosure-recipient-detail-label">Last viewed</span>
                          <span className="disclosure-recipient-detail-value">
                            {r.lastViewedAtDisplay || formatDate(r.lastViewedAt)}
                          </span>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Edit cover-sheet instructions modal. Free-text, saved on confirm;
          server-side length validation surfaces as a notification. */}
      {isEditingInstructions && (
        <div className="disclosure-confirm-overlay" onClick={() => !isSavingInstructions && setIsEditingInstructions(false)}>
          <div
            className="disclosure-confirm-card disclosure-instructions-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="disclosure-confirm-title">Edit instructions</div>
            <div className="disclosure-confirm-body">
              Shown on the cover sheet below the document list.
            </div>
            <textarea
              className="disclosure-instructions-textarea"
              rows={5}
              value={instructionsDraft}
              onChange={(e) => setInstructionsDraft(e.target.value)}
              placeholder="e.g. Submit offers through the Offers link on MLS by Oct 14, 2026, 5:00 PM MT."
              disabled={isSavingInstructions}
              autoFocus
            />
            <div className="disclosure-confirm-actions">
              <button
                type="button"
                className="disclosure-confirm-btn disclosure-confirm-cancel"
                onClick={() => setIsEditingInstructions(false)}
                disabled={isSavingInstructions}
              >
                Cancel
              </button>
              <button
                type="button"
                className="disclosure-confirm-btn disclosure-confirm-primary"
                onClick={handleSaveInstructions}
                disabled={isSavingInstructions}
              >
                {isSavingInstructions ? 'Saving…' : 'Save instructions'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Branded confirm dialog — replaces native window.confirm. Serves both
          the file-delete and share-revoke flows via the shared `confirm` state. */}
      {confirm.visible && (
        <div className="disclosure-confirm-overlay" onClick={closeConfirm}>
          <div className="disclosure-confirm-card" onClick={(e) => e.stopPropagation()}>
            <div className={`disclosure-confirm-icon ${confirm.danger ? 'danger' : ''}`}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </div>
            <div className="disclosure-confirm-title">{confirm.title}</div>
            <div className="disclosure-confirm-body">{confirm.body}</div>
            <div className="disclosure-confirm-actions">
              <button
                type="button"
                className="disclosure-confirm-btn disclosure-confirm-cancel"
                onClick={closeConfirm}
                disabled={confirm.isBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`disclosure-confirm-btn ${confirm.danger ? 'disclosure-confirm-danger' : 'disclosure-confirm-primary'}`}
                onClick={runConfirm}
                disabled={confirm.isBusy}
              >
                {confirm.isBusy ? 'Working…' : confirm.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisclosureTab;
